import React, { useCallback } from 'react';
import { List, Spin } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { AIChatChevronLeftIcon } from '@/assets/icons/icons';
import type {
  AgentCoreChatDrilldownAlert,
  AgentCoreChatDrilldownResult,
  AgentCoreChatDrilldownShipment,
} from '@/apis/agentcoreChat';
import ShipmentListItem from '@/pages/HomeV2/partials/ChartPanel/ShipmentListItem';
import type { Shipment as HomeShipment } from '@/pages/HomeV2/types';
import { getFormattedUserTimeZone } from '@/common/utils/utils';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';
import '@/pages/HomeV2/partials/ChartPanel/HomeChartShipment.css';

// Loaded here too (not just wherever Home happens to load it first) --
// dayjs plugin registration is global but only takes effect once a module
// actually runs .extend(), and chat can render this drilldown without the
// Home page's own module ever having loaded in this session.
dayjs.extend(utc);
dayjs.extend(timezone);

type Props = {
  category: string;
  loading: boolean;
  error: string | null;
  data: AgentCoreChatDrilldownResult | null;
  onBack: () => void;
  onSelectShipment: (shipmentId: string) => void;
  // Directional entrance animation class -- the caller owns the
  // forward/backward decision (it knows whether this mount came from
  // drilling in or backing out), this component just applies it.
  viewEnterClassName: string;
};

function formatDate(value: string | null | undefined): string {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Maps the chat drilldown's flat row shape onto the richer `Shipment` type
// ShipmentListItem (Home's own shipment row, reused as-is here for visual
// uniformity) expects. `type`/`teams`/`alert_status` now come straight from
// the backend (same batched-query pattern Home's own drilldown uses -- see
// AgentCoreChatDrilldownAPIView), so the status pill's color and the "+1"
// team/alert badges render exactly as they do on Home.
function toHomeShipment(item: AgentCoreChatDrilldownShipment): HomeShipment {
  return {
    id: item.id,
    date: item.created_at,
    reference_number: item.reference_number,
    status: item.status,
    delivery_status: item.delivery_status,
    type: Number(item.type) as HomeShipment['type'],
    alert_status: item.alert_status,
    actual_delivery_date: item.actual_delivery_date ?? '',
    estimated_delivery_date: item.estimated_delivery_date ?? '',
    from_location: { id: '', name: item.origin_location },
    to_location: { id: '', name: item.destination_location },
    teams: item.teams,
    leg_type: 'CL',
    alert_type: '',
    total_count: '',
    category_counts: {},
    segment_start: '',
    segment_end: '',
  };
}

// item.id is the alert's parent shipment's id (not the alert's own id) --
// there's no standalone alert-detail view anywhere in the app, so clicking
// an alert row opens that shipment's detail (see AgentCoreChatDrilldownAPIView).
const AlertRow: React.FC<{
  item: AgentCoreChatDrilldownAlert;
  onSelect: (shipmentId: string) => void;
}> = ({ item, onSelect }) => (
  <button
    type='button'
    className='ai-chat-drawer__drilldown-item ai-chat-drawer__drilldown-item--clickable'
    onClick={() => onSelect(item.id)}
  >
    <div className='ai-chat-drawer__drilldown-item-main'>
      <span className='ai-chat-drawer__drilldown-item-ref'>
        {item.reference_number}
      </span>
      <span className='ai-chat-drawer__drilldown-item-status'>
        {item.is_resolved ? 'Resolved' : item.is_critical ? 'Critical' : 'Open'}
      </span>
    </div>
    <div className='ai-chat-drawer__drilldown-item-route'>{item.title}</div>
    <div className='ai-chat-drawer__drilldown-item-meta'>
      {item.courier} · {formatDate(item.created_at)}
    </div>
  </button>
);

/** The drawer's body swaps into this view when a chart data point with a
 * `drilldown` is clicked (see ChatMessageChart) -- same container as
 * the chat thread, not an overlay on top of it, with a back action to
 * return. Renders whichever entity the point's drilldown named (shipment
 * or alert), not something the caller has to know in
 * advance. */
const AIChatChartDrilldown: React.FC<Props> = ({
  category,
  loading,
  error,
  data,
  onBack,
  onSelectShipment,
  viewEnterClassName,
}) => {
  const { formatDateTime } = useUserDateTimeFormat();
  // Matches HomeChartShipmentsList's own getFormattedTime exactly, for the
  // same "Delivered: Jul 29, 6:09 AM PDT" style ShipmentListItem already
  // renders on Home -- reused here rather than reinvented so a future
  // format change made there doesn't quietly diverge from chat.
  const getFormattedTime = useCallback(
    (ts?: string | null): string => {
      if (!ts) return '---';
      const tzString = getFormattedUserTimeZone();
      const match = tzString.match(/ ([A-Za-z_/]+)$/);
      const userTz = match ? match[1] : dayjs.tz.guess();
      const dt = dayjs(ts, 'YYYY-MM-DD HH:mm:ss');
      if (!dt.isValid()) return '---';
      const abbr = new Intl.DateTimeFormat('en-US', {
        timeZone: userTz,
        timeZoneName: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
        .formatToParts(new Date(ts))
        .find((part) => part.type === 'timeZoneName')?.value;
      return (
        formatDateTime(dt, { includeYear: false, includeTime: true }) +
        (abbr ? ` ${abbr}` : '')
      );
    },
    [formatDateTime]
  );

  return (
    <div className={`ai-chat-drawer__drilldown ${viewEnterClassName}`}>
      <div className='ai-chat-drawer__drilldown-header'>
        <button
          type='button'
          className='ai-chat-drawer__drilldown-back-btn'
          onClick={onBack}
          aria-label='Back to chat'
        >
          <AIChatChevronLeftIcon width={16} height={16} color='currentColor' />
        </button>
        <span className='ai-chat-drawer__drilldown-title'>{category}</span>
      </div>
      {loading && (
        <div className='ai-chat-drawer__drilldown-loading'>
          <Spin size='small' />
          <span>Loading...</span>
        </div>
      )}
      {!loading && error && (
        <div className='ai-chat-drawer__drilldown-error'>{error}</div>
      )}
      {!loading && !error && data && data.results.length === 0 && (
        <div className='ai-chat-drawer__drilldown-empty'>
          No matching records found.
        </div>
      )}
      {!loading &&
        !error &&
        data &&
        data.results.length > 0 &&
        (data.entity === 'shipment' ? (
          // Matches HomeChartShipmentsList's own <List>/<List.Item> wrapping
          // around ShipmentListItem exactly -- the row separators come from
          // antd's own List.Item styling, not from HomeChartShipment.css,
          // so skipping this wrapper (as an earlier version of this file
          // did) silently drops them.
          <List
            dataSource={data.results}
            renderItem={(item) => (
              <List.Item key={item.id}>
                <ShipmentListItem
                  item={toHomeShipment(item)}
                  isHome
                  tabValue=''
                  onClick={onSelectShipment}
                  getFormattedTime={getFormattedTime}
                />
              </List.Item>
            )}
          />
        ) : (
          <div className='ai-chat-drawer__drilldown-list'>
            {data.results.map((item) => (
              <AlertRow key={item.id} item={item} onSelect={onSelectShipment} />
            ))}
          </div>
        ))}
    </div>
  );
};

export default AIChatChartDrilldown;
